'use client';

import { useTranslations } from 'next-intl';
import { Pencil, Trash2, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { Shelf } from '../types';

interface ShelfCompactRowProps {
  shelf: Shelf;
  onEdit: (shelf: Shelf) => void;
  onDelete: (shelf: Shelf) => void;
  onMarkMaintenance: (shelf: Shelf) => void;
}

export function ShelfCompactRow({
  shelf,
  onEdit,
  onDelete,
  onMarkMaintenance
}: ShelfCompactRowProps) {
  const t = useTranslations('shelves');

  const statusStyles: Record<Shelf['status'], string> = {
    active:
      'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300 border-blue-200 dark:border-blue-800',
    full: 'bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300 border-red-200 dark:border-red-800',
    maintenance: 'bg-muted text-muted-foreground border-border'
  };

  return (
    <div className='flex items-center gap-3 rounded-md border px-4 py-2.5'>
      <div className='min-w-0 flex-1'>
        <div className='flex items-center gap-2'>
          <span className='font-mono text-sm font-bold'>
            {shelf.shelf_code}
          </span>
          {shelf.storage_location && (
            <span className='text-muted-foreground hidden truncate text-sm sm:inline'>
              {shelf.storage_location.name}
            </span>
          )}
        </div>
        {shelf.shelf_type && (
          <p className='text-muted-foreground truncate text-xs'>
            {shelf.shelf_type}
          </p>
        )}
      </div>

      <span className='text-muted-foreground hidden text-sm md:inline'>
        {shelf.current_capacity}/{shelf.max_capacity}
      </span>

      <span
        className={cn(
          'shrink-0 rounded-full border px-2.5 py-0.5 text-xs font-medium',
          statusStyles[shelf.status]
        )}
      >
        {t(`status.${shelf.status}`)}
      </span>

      <div className='flex shrink-0 items-center gap-1'>
        <Button
          variant='ghost'
          size='icon'
          className='h-7 w-7'
          onClick={() => onEdit(shelf)}
        >
          <Pencil className='h-3.5 w-3.5' />
        </Button>
        {shelf.status !== 'maintenance' && (
          <Button
            variant='ghost'
            size='icon'
            className='h-7 w-7'
            onClick={() => onMarkMaintenance(shelf)}
          >
            <Wrench className='h-3.5 w-3.5' />
          </Button>
        )}
        <Button
          variant='ghost'
          size='icon'
          className='text-destructive h-7 w-7'
          onClick={() => onDelete(shelf)}
        >
          <Trash2 className='h-3.5 w-3.5' />
        </Button>
      </div>
    </div>
  );
}
